import React, { useState } from 'react';
import { Terminal, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Activity } from 'lucide-react';

const EdgeFunctionsMonitor = ({ functions, logs }) => {
  const [activeTab, setActiveTab] = useState('functions');
  const [expandedLog, setExpandedLog] = useState(null);

  const getStatusIcon = (status) => {
    if (status === 'success') return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
    if (status === 'error') return <XCircle className="w-3.5 h-3.5 text-red-500" />;
    return <Clock className="w-3.5 h-3.5 text-yellow-500" />;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100">
        <Terminal className="w-5 h-5 text-indigo-600" />
        <h2 className="text-base font-semibold text-gray-900">Edge Functions Monitor</h2>
        <div className="ml-auto flex gap-1 bg-gray-100 rounded-lg p-1">
          {['functions', 'logs']?.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      {activeTab === 'functions' && (
        <div className="divide-y divide-gray-50">
          {functions?.map((fn) => (
            <div key={fn?.id} className="px-6 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Activity className="w-4 h-4 text-indigo-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{fn?.name}</p>
                    <p className="text-xs text-gray-400">{fn?.endpoint}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right hidden md:block">
                    <p className="text-xs text-gray-400">Invocations</p>
                    <p className="text-sm font-medium text-gray-700">{fn?.invocations}</p>
                  </div>
                  <div className="text-right hidden md:block">
                    <p className="text-xs text-gray-400">Avg Latency</p>
                    <p className="text-sm font-medium text-gray-700">{fn?.avgLatency}</p>
                  </div>
                  <div className="text-right hidden lg:block">
                    <p className="text-xs text-gray-400">Error Rate</p>
                    <p className={`text-sm font-medium ${parseFloat(fn?.errorRate) > 2 ? 'text-red-600' : 'text-green-600'}`}>
                      {fn?.errorRate}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    fn?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {fn?.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {activeTab === 'logs' && (
        <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
          {logs?.map((log) => (
            <div key={log?.id} className="px-6 py-3">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedLog(expandedLog === log?.id ? null : log?.id)}
              >
                <div className="flex items-center gap-2">
                  {getStatusIcon(log?.status)}
                  <span className="text-xs font-mono text-gray-600">{log?.timestamp}</span>
                  <span className="text-xs font-medium text-gray-800">{log?.function}</span>
                  <span className="text-xs text-gray-500">{log?.message}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{log?.duration}</span>
                  {expandedLog === log?.id ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                </div>
              </div>
              {expandedLog === log?.id && (
                <div className="mt-2 p-2 bg-gray-900 rounded-md">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{log?.details}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EdgeFunctionsMonitor;
